import React, { useState } from "react";
import { educationSchema } from "./EasyApplyValidationSchema";
import { useFormikContext } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import infoIcon from "../../assets/images/JobIcon/info.svg";
import { Plus } from "lucide-react";

// Type definitions for education
export interface EducationEntry {
  year: number | string | null;
  id: string;
  institution: string;
  major: string;
  degree: string;
  location: string;
  description: string;
  currentlyAttending: boolean;
  startDate: string;
  endDate: string | null;
}

export interface EducationErrors {
  institution?: string;
  major?: string;
  degree?: string;
  location?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  currentlyAttending?: string;
  year?: string;
}

interface EducationProps {
  education: EducationEntry[];
}

const Education: React.FC<EducationProps> = ({ education }) => {
  const { setFieldValue, errors, touched } = useFormikContext<{
    education: EducationEntry[];
  }>();

  const [isEducationFormVisible, setIsEducationFormVisible] =
    useState<boolean>(false);

  const [editingEducationIndex, setEditingEducationIndex] = useState<
    number | null
  >(null);
  const [educationFormErrors, setEducationFormErrors] =
    useState<EducationErrors>({});
  const [educationDraft, setEducationDraft] = useState<EducationEntry>({
    id: "",
    institution: "",
    major: "",
    degree: "",
    location: "",
    description: "",
    currentlyAttending: false,
    startDate: "",
    endDate: null,
    year: "",
  });

  const handleAdd = () => {
    setEducationDraft({
      id: Date.now().toString(),
      institution: "",
      major: "",
      degree: "",
      location: "",
      description: "",
      currentlyAttending: false,
      startDate: "",
      endDate: null,
      year: "",
    });
    setEditingEducationIndex(null);
    setIsEducationFormVisible(true);
    setEducationFormErrors({});
  };

  const handleEdit = (educationEntry: EducationEntry, index: number) => {
    setEducationDraft({
      ...educationEntry,
      endDate: educationEntry.endDate || null,
    });
    setEditingEducationIndex(index);
    setIsEducationFormVisible(true);
    setEducationFormErrors({});
  };

  const validateEducation = async (
    education: EducationEntry
  ): Promise<boolean> => {
    try {
      await educationSchema.validate(education, { abortEarly: false });
      setEducationFormErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: EducationErrors = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof EducationErrors] = err.message;
      });
      setEducationFormErrors(validationErrors);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateEducation(educationDraft);
    if (!isValid) return;

    let updatedEducation: EducationEntry[];
    if (editingEducationIndex !== null) {
      updatedEducation = education.map((edu, idx) =>
        idx === editingEducationIndex ? educationDraft : edu
      );
    } else {
      updatedEducation = [
        ...education,
        { ...educationDraft, id: Date.now().toString() },
      ];
    }
    setFieldValue("education", updatedEducation);
    setIsEducationFormVisible(false);
    setEditingEducationIndex(null);
    setEducationFormErrors({});
  };

  const handleCancel = () => {
    setIsEducationFormVisible(false);
    setEditingEducationIndex(null);
    setEducationFormErrors({});
    setEducationDraft({
      id: "",
      institution: "",
      major: "",
      degree: "",
      location: "",
      description: "",
      currentlyAttending: false,
      startDate: "",
      endDate: null,
      year: "",
    });
  };

  const handleDelete = (index: number) => {
    const updatedEducation = education.filter((_, id) => id !== index);
    setFieldValue("education", updatedEducation);
  };

  const handleTempChange = (
    field: keyof EducationEntry,
    value: string | boolean | null
  ) => {
    setEducationDraft((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "currentlyAttending" && value === true
        ? { endDate: null }
        : {}),
    }));

    if (educationFormErrors[field as keyof EducationErrors]) {
      setEducationFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const getFieldError = (fieldName: keyof EducationErrors) => {
    return educationFormErrors[fieldName];
  };

  const isFieldInvalid = (fieldName: keyof EducationErrors) => {
    return !!educationFormErrors[fieldName];
  };

  return (
    <div className="form-section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="section-title mb-0">Education</h4>
        <button
          type="button"
          className="pill-btn pill-btn--accent d-flex align-items-center gap-2"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add education
        </button>
      </div>

      {/* Display existing education entries */}
      {education.map((edu, index) => (
        <div key={edu.id} className="mb-4 p-3 border rounded bg-light">
          <div className="d-flex align-items-start gap-3">
            <div className="education-icon">
              {/* <img
                alt="Education"
                width={40}
                height={40}
              /> */}
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-1">{edu.institution}</h6>
              <p className="text-muted mb-1">
                {edu.major} - {edu.degree}
              </p>
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                {edu.startDate} -{" "}
                {edu.currentlyAttending ? "Present" : edu.endDate || "N/A"}
              </p>
              {errors.education?.[index] && touched.education?.[index] && (
                <div className="error-message mt-2">
                  {Object.values(errors.education[index] as EducationErrors)
                    .filter((err) => err)
                    .join(", ")}
                </div>
              )}
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="pill-btn pill-btn--ghost"
                onClick={() => handleEdit(edu, index)}
              >
                Edit
              </button>
              <button
                type="button"
                className="pill-btn pill-btn--danger"
                onClick={() => handleDelete(index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Education Form */}
      {isEducationFormVisible && (
        <div className="mb-4">
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">
                Institution <span className="required-field">*</span>
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className={`form-control ${
                    isFieldInvalid("institution") ? "is-invalid" : ""
                  }`}
                  value={educationDraft.institution}
                  onChange={(e) =>
                    handleTempChange("institution", e.target.value)
                  }
                  name={
                    editingEducationIndex !== null
                      ? `education[${editingEducationIndex}].institution`
                      : undefined
                  }
                  placeholder="Enter Institution"
                />
                <div className="position-absolute end-0 top-50 translate-middle-y me-2"></div>
              </div>
              {getFieldError("institution") && (
                <div className="error-message">
                  {/* <img src={infoIcon} alt="Info" width={12} height={12} /> */}
                  {getFieldError("institution")}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Major</label>
              <input
                type="text"
                className={`form-control ${
                  isFieldInvalid("major") ? "is-invalid" : ""
                }`}
                placeholder="Enter major"
                value={educationDraft.major}
                onChange={(e) => handleTempChange("major", e.target.value)}
                name={
                  editingEducationIndex !== null
                    ? `education[${editingEducationIndex}].major`
                    : undefined
                }
              />
              {getFieldError("major") && (
                <div className="error-message">{getFieldError("major")}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Degree</label>
              <input
                type="text"
                className={`form-control ${
                  isFieldInvalid("degree") ? "is-invalid" : ""
                }`}
                placeholder="Enter degree"
                value={educationDraft.degree}
                onChange={(e) => handleTempChange("degree", e.target.value)}
                name={
                  editingEducationIndex !== null
                    ? `education[${editingEducationIndex}].degree`
                    : undefined
                }
              />
              {getFieldError("degree") && (
                <div className="error-message">{getFieldError("degree")}</div>
              )}
            </div>

            <div className="col-12">
              <label className="form-label">School Location</label>
              <div className="position-relative">
                <input
                  type="text"
                  className={`form-control ${
                    isFieldInvalid("location") ? "is-invalid" : ""
                  }`}
                  placeholder="Enter school location"
                  value={educationDraft.location}
                  onChange={(e) => handleTempChange("location", e.target.value)}
                  name={
                    editingEducationIndex !== null
                      ? `education[${editingEducationIndex}].location`
                      : undefined
                  }
                />
                <div className="position-absolute end-0 top-50 translate-middle-y me-2"></div>
              </div>
              {getFieldError("location") && (
                <div className="error-message">{getFieldError("location")}</div>
              )}
            </div>

            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  isFieldInvalid("description") ? "is-invalid" : ""
                }`}
                placeholder="Write here..."
                rows={4}
                value={educationDraft.description}
                onChange={(e) =>
                  handleTempChange("description", e.target.value)
                }
              />
              {getFieldError("description") && (
                <div className="error-message">
                  <img src={infoIcon} alt="Info" width={12} height={12} />
                  {getFieldError("description")}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                From <span className="required-field">*</span>
              </label>
              <div
                className={`date-picker-shell ${
                  isFieldInvalid("startDate") ? "has-error" : ""
                }`}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      educationDraft.startDate
                        ? dayjs(educationDraft.startDate)
                        : null
                    }
                    onChange={(newValue: Dayjs | null) => {
                      const formatted = newValue
                        ? newValue.format("YYYY-MM-DD")
                        : "";
                      handleTempChange("startDate", formatted);
                      if (
                        educationDraft.endDate &&
                        formatted &&
                        dayjs(educationDraft.endDate).isBefore(dayjs(formatted))
                      ) {
                        handleTempChange("endDate", formatted);
                      }
                    }}
                    disableFuture
                    minDate={dayjs("1900-01-01")}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "Pick a date",
                        variant: "outlined",
                        className: "date-picker-input",
                        size: "small",
                        fullWidth: true,
                        inputProps: {
                          className: "date-picker-input-field",
                          placeholder: "Pick a date",
                        },
                      } as any,
                      openPickerIcon: { className: "date-picker-icon" },
                    }}
                  />
                </LocalizationProvider>
              </div>
              {getFieldError("startDate") && (
                <div className="error-message">
                  {getFieldError("startDate")}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                To <span className="required-field">*</span>
              </label>
              <div
                className={`date-picker-shell ${
                  isFieldInvalid("endDate") ? "has-error" : ""
                }`}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      educationDraft.endDate
                        ? dayjs(educationDraft.endDate)
                        : null
                    }
                    onChange={(newValue: Dayjs | null) => {
                      const formatted = newValue
                        ? newValue.format("YYYY-MM-DD")
                        : "";
                      handleTempChange("endDate", formatted || null);
                    }}
                    disabled={educationDraft.currentlyAttending}
                    minDate={
                      educationDraft.startDate
                        ? dayjs(educationDraft.startDate)
                        : dayjs("1900-01-01")
                    }
                    disableFuture
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "Pick a date",
                        variant: "outlined",
                        className: "date-picker-input",
                        size: "small",
                        fullWidth: true,
                        inputProps: {
                          className: "date-picker-input-field",
                          placeholder: "Pick a date",
                        },
                      } as any,
                      openPickerIcon: { className: "date-picker-icon" },
                    }}
                  />
                </LocalizationProvider>
              </div>
              {getFieldError("endDate") && (
                <div className="error-message">{getFieldError("endDate")}</div>
              )}
            </div>

            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={educationDraft.currentlyAttending}
                  onChange={(e) =>
                    handleTempChange("currentlyAttending", e.target.checked)
                  }
                  id="currentlyAttending"
                  name={
                    editingEducationIndex !== null
                      ? `education[${editingEducationIndex}].currentlyAttending`
                      : undefined
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="currentlyAttending"
                >
                  I currently attend
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button
              type="button"
              className="pill-btn pill-btn--ghost"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pill-btn pill-btn--primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Education;
